import { Form } from 'antd';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { SimulateUseCaseInterface } from 'application/usecases/SimulateUseCase';
import DropdownIcon from 'assets/icons/simulate_dropdown.svg';
import DropupIcon from 'assets/icons/simulate_dropup.svg';
import { SystemSetting } from 'domain/entities/MasterEntity';
import { OptionEntity } from 'domain/entities/OptionEntity';
import {
    CaseSimulateEntity,
    SimHistoryInfo,
    SimulateBreakEntity,
} from 'domain/entities/SimulateEntity';
import moment from 'moment';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import {
    completeFetching,
    showConfirm,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { AUTH_ROUTES } from 'presentation/router/CONSTANTS';
import React from 'react';
import { useCollapse } from 'react-collapsed';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { SimHistoryModal } from '../../CU-010-02/components/SimHistoryModal';
import { SimInputTitleModal } from '../../CU-010-02/components/SimInputTitleModal';
import ExtendForm from './ExtendForm';
import FormSimulateSale from './FormSimulateSale';

type props = {
    form: any;
    service: SimulateUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
};

const OPTION_SETTINGS = [
    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_YIELD_AT_TIME_OF_SALE_1',
    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_YIELD_AT_TIME_OF_SALE_2',
    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_YIELD_AT_TIME_OF_SALE_3',
];

const ALL_SETTINGS = [
    ...OPTION_SETTINGS,
    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_DEFAULT_RENT_CHANGE_RATE',
    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_DEFAULT_MANAGEMENT_FEE_CHANGE_AMOUNT',
];

interface ItemSetting {
    min: number;
    max: number;
}

const Content = ({ form, service, masterUseCase }: props) => {
    const { simId } = useParams();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
    const [descData, setDescData] = React.useState<CaseSimulateEntity[]>([]);
    const [dataChart, setDataChart] = React.useState<any[]>([
        {
            id: 'japan',
            color: 'hsl(262, 70%, 50%)',
            data: [],
        },
    ]);

    const userSlice = useAppSelector((state) => state.user);
    const [selectedHistorySimId, setSelectedHistorySimId] = React.useState<number>();
    const [historyList, setHistoryList] = React.useState<SimHistoryInfo[]>();
    const [openSimHistoryModal, setOpenSimHistoryModal] = React.useState<boolean>(false);
    const [openSimInputTitleModal, setOpenSimInputTitleModal] = React.useState<boolean>(false);
    const [btnDisable, setBtnDisable] = React.useState<boolean>(false);
    const [simInteresOptions, setSimInteresOptiOptions] = React.useState<any[]>([]);
    const [displayInterest, setDisplayInterest] = React.useState<CaseSimulateEntity>();
    const [rentChangeSetting, setRentChangeSetting] = React.useState<ItemSetting>();
    const [managementFeeChangeSetting, setManagementFeeChangeSetting] =
        React.useState<ItemSetting>();
    const [form2] = Form.useForm();

    const getData = async (payload: SimulateBreakEntity) => {
        try {
            dispatch(startFetching());
            const fluctuationRate = form2.getFieldValue('fluctuationRate')
                ? Number(replace(form2.getFieldValue('fluctuationRate') + ''))
                : 0;
            const managementFeeFluctuationAmount = form2.getFieldValue(
                'managementFeeFluctuationAmount',
            )
                ? Number(replace(form2.getFieldValue('managementFeeFluctuationAmount') + ''))
                : 0;
            const response = await service.getSimulateBreak({
                ...payload,
                fluctuationRate: fluctuationRate,
                managementFeeFluctuationAmount: managementFeeFluctuationAmount,
            });
            const data = response.result;
            setDescData(data.caseSimulations);

            // Form set field
            form.setFieldsValue({
                drawdownDate: moment(data.drawdownDate),
                loanReceivable: commonHelpers.formatNumberMoney(data.loanReceivable),
                annualInterest: commonHelpers.formatNumberMoney(data.annualInterest),
                rent: commonHelpers.formatNumberMoney(data.rent),
                managementFee: commonHelpers.formatNumberMoney(data.managementFee),
                repaymentPeriod: commonHelpers.formatNumberMoney(data.repaymentPeriod),
                fluctuationRate: commonHelpers.formatNumberMoney(data.fluctuationRate),
                managementFeeFluctuationAmount: commonHelpers.formatNumberMoney(
                    data.managementFeeFluctuationAmount,
                ),
                title: data.simulationTitle,
            });
            const dC = data.caseSimulations.map((item: CaseSimulateEntity) => {
                return {
                    x: `${item.passedYears}`,
                    y: item.salesPrice,
                };
            });

            // Set Data Chart
            setDataChart([
                {
                    id: 'japan',
                    color: 'hsl(262, 70%, 50%)',
                    data: dC,
                },
            ]);
            const displayInterest = data.caseSimulations.find(
                (item) => item.interest == form2.getFieldValue('simCase'),
            );
            setDisplayInterest(displayInterest);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const replace = (value: string) => {
        return value.replaceAll(',', '');
    };

    React.useEffect(() => {
        const payload = {
            ...location.state,
            simId: simId ?? null,
        };
        getOptions();
        getData(payload);
    }, []);

    const getOptions = async () => {
        try {
            dispatch(startFetching());
            const response = await masterUseCase.getSystemSettingList({
                keys: ALL_SETTINGS,
            });
            const options = response
                .filter((item: SystemSetting) => {
                    return OPTION_SETTINGS.includes(item.key);
                })
                .map((item: SystemSetting) => {
                    return {
                        label: `利回り${item.value}%`,
                        value: Number(item.value) / 100,
                    } as OptionEntity;
                });
            setSimInteresOptiOptions(options);
            form2.setFieldsValue({
                simCase: options[0].value,
            });
            const rentSystemSetting = response.find(
                (item: SystemSetting) =>
                    item.key ===
                    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_DEFAULT_RENT_CHANGE_RATE',
            );
            const rentChangeSetting = {
                min:
                    Number(rentSystemSetting?.value) - 5 > 0
                        ? Number(rentSystemSetting?.value) - 5
                        : 0,
                max: Number(rentSystemSetting?.value) + 5,
            };
            setRentChangeSetting(rentChangeSetting);
            const managementFeeChangeSetting = response.find(
                (item: SystemSetting) =>
                    item.key ===
                    'SIMULATOR_PROFIT_AND_LOSS_BREAK_EVEN_SIM_DEFAULT_MANAGEMENT_FEE_CHANGE_AMOUNT',
            );
            const managementFeeChangeSettingData = {
                min: 0,
                max: Number(managementFeeChangeSetting?.value),
            };
            setManagementFeeChangeSetting(managementFeeChangeSettingData);
            form2.setFieldsValue({
                fluctuationRate: commonHelpers.formatInputNumber(rentSystemSetting?.value),
                managementFeeFluctuationAmount: commonHelpers.formatInputNumber(
                    managementFeeChangeSetting?.value,
                ),
            });
            // eslint-disable-next-line no-empty
        } catch (err) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const scrollTop = () => {
        document.getElementById('top-page')?.scrollIntoView({ behavior: 'smooth' });
    };

    const checkLogin = async () => {
        if (userSlice?.isSignedIn) {
            const value = form.getFieldsValue();
            const payload = {
                ...value,
                loanReceivable: Number(replace(value.loanReceivable)),
                annualInterest: Number(replace(value.annualInterest)),
                rent: Number(replace(value.rent)),
                managementFee: Number(replace(value.managementFee)),
            };
            if (simId) {
                saveData(form.getFieldValue('title'));
            } else {
                openHistoryPopup(payload);
            }
        } else {
            const confirmProps = {
                message: '以降のステップには\nログインが必要です。',
                okText: 'ログイン・アカウント作成へ',
                cancelText: 'いいえ',
                type: 'info',
                onOk: goToLoginPage,
            };
            dispatch(showConfirm(confirmProps));
        }
    };

    const openHistoryPopup = async (values: SimulateBreakEntity) => {
        try {
            dispatch(startFetching());
            const payload: SimulateBreakEntity = {
                ...values,
                drawdownDate: moment().format(DATE_FORMAT.FORMAT1),
                simId: null,
            };
            const responses = await Promise.all([
                service.getSimModalList('CU-053-02'),
                getData(payload),
            ]);
            const response = responses[0];
            if (response.result) {
                setHistoryList(response.result.listSim);
            }
            // eslint-disable-next-line no-empty
        } catch (err) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const goToLoginPage = () => {
        navigate(`${AUTH_ROUTES.PATH}/${AUTH_ROUTES.SUB.CU_001_00}`);
    };

    React.useEffect(() => {
        if (historyList) {
            if (historyList.length >= 10) {
                setOpenSimHistoryModal(true);
            } else {
                openInputPopup();
            }
        }
    }, [historyList]);

    const openInputPopup = async (simId?: number) => {
        setSelectedHistorySimId(simId);
        setHistoryList(undefined);
        setOpenSimHistoryModal(false);
        setOpenSimInputTitleModal(true);
    };

    const saveData = async (title: string) => {
        try {
            setOpenSimInputTitleModal(false);
            const values = form.getFieldsValue();
            dispatch(startFetching());
            const payload: SimulateBreakEntity = {
                drawdownDate: moment().format(DATE_FORMAT.FORMAT1),
                loanReceivable: Number(replace(values.loanReceivable)),
                annualInterest: Number(replace(values.annualInterest)),
                rent: Number(replace(values.rent)),
                managementFee: Number(replace(values.managementFee)),
                repaymentPeriod: Number(replace(values.repaymentPeriod)),
                fluctuationRate: 0,
                managementFeeFluctuationAmount: 0,
                simId: simId ? Number(simId) : selectedHistorySimId ?? null,
                title: title,
                simulationTitle: title,
            };
            await service.createSimulateBreak(payload);
            setBtnDisable(true);
            openSuccessNoti({ description: getMessage('S338') });
            // eslint-disable-next-line no-empty
        } catch (error: any) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const onChangeOption = (value: any) => {
        const displayInterest = descData.find((item) => item.interest === value);
        setDisplayInterest(displayInterest);
    };

    return (
        <div className="content" id="top-page">
            <ExtendForm
                dataChart={dataChart}
                form={form2}
                managementFeeChangeSetting={managementFeeChangeSetting}
                onChangeOption={onChangeOption}
                rentChangeSetting={rentChangeSetting}
                simInteresOptions={simInteresOptions}
                displayInterest={displayInterest}
            />

            <div className="content-collapse" style={{ marginBottom: 30 }}>
                <div className="content-collapse__title">入力条件</div>
                <div className="content-collapse__icon" {...getToggleProps()}>
                    <img src={isExpanded ? DropupIcon : DropdownIcon} alt="dropdown icon" />
                </div>
            </div>
            <FormSimulateSale
                form={form}
                getCollapseProps={getCollapseProps}
                service={service}
                onSubmit={getData}
                saveData={checkLogin}
                scrollTop={scrollTop}
                btnDisable={btnDisable}
                setBtnDisable={setBtnDisable}
            />
            <SimHistoryModal
                data={historyList}
                open={openSimHistoryModal}
                onOk={openInputPopup}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimHistoryModal(false);
                }}
            />
            <SimInputTitleModal
                open={openSimInputTitleModal}
                onOk={saveData}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimInputTitleModal(false);
                }}
            />
        </div>
    );
};

export default Content;
